import React, {
  useEffect,
  useMemo
} from 'react'
import PropTypes from 'prop-types'
import { useQuery } from 'react-apollo'
import { propType } from 'graphql-anywhere'
import classNames from 'classnames'
import { Helmet } from 'react-helmet'
import { replace } from 'ramda'

import DetailRateButtonAndModal from '../../modules/user-rating/detail-rate-modal'
import HeroImage from '../shared/hero-image'
import ShowPageHeading from '../shared/show-page-heading'
import ShowPageStatus from '../shared/show-page-status'
import { WatchButton, ReplayButton } from '../../containers/play-button/showpage-play-button'
import AddOrRemoveFromListButton from '../showpage-buttons/add-or-remove-from-list-button'
import {
  getFirstTrailer,
  getEnableAutoplayTrailer,
  getSeasonRange,
  getYearRange,
  getDetailsFullUrl,
  pluralize,
  getTwoGenres
} from '../../lib/utils'

import styles from './series-detail.css'

import CONTENT_ITEM_FRAGMENT from '../../../graphql/fragments/content-item.gql'
import { openGraphMetaImages } from '../../lib/open-graph/images'
import { openGraphMetaActors } from '../../lib/open-graph/actors'
import { openGraphMetaTags } from '../../lib/open-graph/tags'
import { openGraphMetaDirectors } from '../../lib/open-graph/directors'
import {
  getLocationBaseWithHref
} from '../../lib/location'
import { PLAY_BUTTON_IN_SERIES_HERO } from '../../containers/play-button/constants'
import {
  COMPONENT_NAME_SERIES_DETAIL
} from '../../lib/analytics/custom-dimensions'
import { E2E_SELECTORS } from '../../e2e-selectors'
import AutoplayTrailerStream from '../autoplay-trailer/autoplay-trailer-stream'
import { useIsAuthenticated } from '../../hooks/use-is-authenticated'
import useInitializeCastSender from '../../hooks/use-initialize-cast-sender'
import PROFILE_PREFERENCES_QUERY from '../../../graphql/queries/profile-preferences.gql'
import useTrackShowPageSideEffects from '../../hooks/useTrackShowPageSideEffects'

function SeriesDetail({
  contentItem,
  margin,
  showSquareHeroImage
}) {
  const isAuthenticated = useIsAuthenticated()
  const profilePreferencesQuery = useQuery(PROFILE_PREFERENCES_QUERY, {
    fetchPolicy: 'cache-first',
    skip: !isAuthenticated
  })
  useTrackShowPageSideEffects({
    contentItem,
    componentName: COMPONENT_NAME_SERIES_DETAIL
  })
  const { castPreviewPageSender } = useInitializeCastSender()
  useEffect(() => {
    castPreviewPageSender(contentItem)
  }, [castPreviewPageSender, contentItem])

  const trailerAutoplayOnShowPageEnabled = profilePreferencesQuery?.data
    ?.profilePreferences?.trailerAutoplayOnShowPageEnabled
  // As per design show only 2 first genres
  const truncatedGenres = getTwoGenres(contentItem)
  const seasonCount = pluralize(contentItem.seasons.length, 'SEASON', 'S')
  const yearRange = getYearRange(contentItem.seasons)
  const editedYearRange = yearRange && yearRange.length <= 6 ? replace(/[()]/g, '', yearRange) : yearRange
  const badges = [...truncatedGenres, seasonCount, yearRange ? editedYearRange : null]
  const imageUri = contentItem.keyart && contentItem.keyart.uri
  const enableAutoplayTrailers = getEnableAutoplayTrailer(contentItem)
  const trailer = getFirstTrailer(enableAutoplayTrailers)
  const brand = 'NEON'
  const title = useMemo(() => `Watch ${contentItem.title} Online | Season ${getSeasonRange(contentItem.seasons, ' - ')} on ${brand}`,
    [contentItem.title, contentItem.seasons])
  const description = useMemo(() => `Watch ${contentItem.title} season ${getSeasonRange(contentItem.seasons, ' - ')} online with ${brand}.`,
    [contentItem.title, contentItem.seasons])

  const autoplayTrailerEnabled = trailerAutoplayOnShowPageEnabled && trailer

  return (
    <>
      <div className={styles.seriesDetail}>
        <Helmet>
          <meta property="og:title" content={title} />
          <meta property="og:type" content="video.tv_show" />
          <meta property="og:url" content={getLocationBaseWithHref()} />
          <meta property="og:video:release_date" content={contentItem.originalAirDate} />
          {openGraphMetaImages(contentItem.images)}
          {openGraphMetaActors(contentItem.actors)}
          {openGraphMetaDirectors(contentItem.directors)}
          {openGraphMetaTags(contentItem.genres)}
          <meta
            name="description"
            property="og:description"
            content={description}
          />
          <link rel="canonical" href={getDetailsFullUrl(contentItem)} />
          <title>{title}</title>
        </Helmet>

        {/* show autoplay trailer when user is logged in and trailer autoplay is enabled */}
        {autoplayTrailerEnabled ? (
          <AutoplayTrailerStream content={trailer} image={imageUri} />
        ) : (
          <HeroImage
            image={imageUri}
            title={contentItem.title}
            aspectRatio="16:8"
            brandLogo={contentItem.brandOrDistributorLogo}
            showSquareHeroImage={showSquareHeroImage}
          />
        )}

        <div className={classNames(styles.header, {
          [styles.headerUnderTrailer]: autoplayTrailerEnabled
        })}
        >
          <div
            className={styles.leftHeader}
            style={{
              marginLeft: margin
            }}
          >
            <ShowPageHeading
              title={contentItem.title}
              titleTreatment={contentItem.titleTreatment}
              brandLogo={contentItem.brandOrDistributorLogo}
              e2eIdentifier={E2E_SELECTORS.SHOW_TITLE}
            />

            <ShowPageStatus
              contentItem={contentItem}
              badges={badges}
            />

            <div className={styles.buttonsWrapper}>
              <WatchButton
                displayedInLocation={PLAY_BUTTON_IN_SERIES_HERO}
                contentItem={contentItem}
              />

              <ReplayButton
                displayedInLocation={PLAY_BUTTON_IN_SERIES_HERO}
                contentItem={contentItem}
              />

              <AddOrRemoveFromListButton
                contentItem={contentItem}
                renderedInComponent={COMPONENT_NAME_SERIES_DETAIL}
              />

              <DetailRateButtonAndModal
                contentItem={contentItem}
                isAuthenticated={isAuthenticated}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

SeriesDetail.propTypes = {
  contentItem: propType(CONTENT_ITEM_FRAGMENT).isRequired,
  margin: PropTypes.number.isRequired,
  showSquareHeroImage: PropTypes.bool.isRequired
}

export default SeriesDetail
